import type { Span } from '@wiztime/core';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { formatDuration, formatRange } from '../../../lib/format';
import type { DragPoint } from './useLaneInteractions';
import styles from './Timeline.module.css';

/** Distance between the mouse cursor and the tip (ui-redesign §3.4.3). */
const CURSOR_OFFSET_PX = 16;
/** A touch drag shows the tip this far above the finger, clear of the hand. */
const TOUCH_OFFSET_PX = 56;
/** Minimum clearance kept from the viewport edges. */
const EDGE_MARGIN_PX = 4;

/** Second line of the tip, by §3.4.3 priority: delete warning > overlap > flip > minimum > hint. */
export interface TipLine {
  readonly text: string;
  /** Warning tone (member deletion with notes, ui-redesign §2.3). */
  readonly warn?: boolean;
}

interface FloatTipProps {
  readonly span: Span;
  /** Pointer position kept by the lane without re-rendering it; the tip follows it itself. */
  readonly pointRef: { readonly current: DragPoint | null };
  readonly line2?: TipLine | null;
}

/**
 * The floating time tip of a drag ("14:00–16:30 · 2.5h"), rendered to `document.body`: it never
 * lives inside a lane, so it cannot change the lane's layout, scrollbars or grid (ui-redesign
 * §3.1.1 L4, §3.4.3). It follows the pointer with its own listener (only the tip re-renders) and
 * flips when it would leave the viewport.
 */
export function FloatTip({ span, pointRef, line2 }: FloatTipProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [point, setPoint] = useState<DragPoint | null>(null);
  const [position, setPosition] = useState({ left: -9999, top: -9999 });

  useEffect(() => {
    // Seed from the position recorded by the lane (the move that started the drag), then follow.
    setPoint(pointRef.current);
    const onMove = (event: PointerEvent): void =>
      setPoint((previous) => ({
        x: event.clientX,
        y: event.clientY,
        touch: previous?.touch ?? event.pointerType === 'touch',
      }));
    window.addEventListener('pointermove', onMove);
    return () => window.removeEventListener('pointermove', onMove);
  }, [pointRef]);

  useLayoutEffect(() => {
    const element = ref.current;
    if (element === null || point === null) return;
    const { width, height } = element.getBoundingClientRect();
    let left = point.touch ? point.x - width / 2 : point.x + CURSOR_OFFSET_PX;
    let top = point.touch ? point.y - TOUCH_OFFSET_PX - height : point.y + CURSOR_OFFSET_PX;
    if (left + width > window.innerWidth - EDGE_MARGIN_PX) left = point.x - CURSOR_OFFSET_PX - width;
    if (top + height > window.innerHeight - EDGE_MARGIN_PX) top = point.y - CURSOR_OFFSET_PX - height;
    left = Math.max(EDGE_MARGIN_PX, left);
    top = Math.max(EDGE_MARGIN_PX, top);
    setPosition((previous) => (previous.left === left && previous.top === top ? previous : { left, top }));
  }, [span, point, line2]);

  if (point === null) return null;
  return createPortal(
    <div
      ref={ref}
      className={styles.floatTip}
      style={{ left: position.left, top: position.top }}
      data-testid="time-bubble"
    >
      <div>
        {formatRange(span.startMin, span.endMin)} · {formatDuration(span.endMin - span.startMin)}
      </div>
      {line2 != null ? (
        <div
          className={line2.warn === true ? styles.floatTipWarn : styles.floatTipLine2}
          data-testid="tip-line2"
        >
          {line2.text}
        </div>
      ) : null}
    </div>,
    document.body,
  );
}
